import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import QuickActionPanel from '../../components/ui/QuickActionPanel';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import ExportActionCenter from '../../components/ui/ExportActionCenter';
import ScoreOverview from './components/ScoreOverview';
import KnowledgeAreaBreakdown from './components/KnowledgeAreaBreakdown';
import PerformanceTrendChart from './components/PerformanceTrendChart';
import WeakAreaIdentification from './components/WeakAreaIdentification';
import QuestionReview from './components/QuestionReview';

const ResultsAnalytics = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const testResult = {
    completedDate: "02/03/2026",
    completedTime: "2:45 PM",
    totalQuestions: 150,
    correctAnswers: 123,
    incorrectAnswers: 27,
    percentage: 82,
    timeTaken: "2h 15m",
    avgTimePerQuestion: "54s"
  };

  const knowledgeAreas = [
    {
      name: "Business Analysis Planning and Monitoring",
      questionsAttempted: 25,
      correct: 22,
      percentage: 88,
      weakTopics: []
    },
    {
      name: "Elicitation and Collaboration",
      questionsAttempted: 30,
      correct: 24,
      percentage: 80,
      weakTopics: ["Stakeholder Engagement", "Elicitation Techniques"]
    },
    {
      name: "Requirements Life Cycle Management",
      questionsAttempted: 28,
      correct: 19,
      percentage: 68,
      weakTopics: ["Requirements Traceability", "Change Control"]
    },
    {
      name: "Strategy Analysis",
      questionsAttempted: 22,
      correct: 20,
      percentage: 91,
      weakTopics: []
    },
    {
      name: "Requirements Analysis and Design Definition",
      questionsAttempted: 30,
      correct: 25,
      percentage: 83,
      weakTopics: ["Requirements Modeling"]
    },
    {
      name: "Solution Evaluation",
      questionsAttempted: 15,
      correct: 13,
      percentage: 87,
      weakTopics: []
    }
  ];

  const trendData = [
    { date: "01/15/2026", score: 65, average: 70 },
    { date: "01/22/2026", score: 72, average: 71 },
    { date: "01/29/2026", score: 78, average: 73 },
    { date: "02/03/2026", score: 82, average: 75 }
  ];

  const weakAreas = [
    {
      knowledgeArea: "Requirements Life Cycle Management",
      severity: "critical",
      currentScore: 68,
      targetScore: 80,
      recommendedQuestions: 50,
      description: "Focus on requirements traceability and change control processes to improve performance in this critical knowledge area.",
      specificTopics: ["Requirements Traceability", "Change Control", "Requirements Approval", "Maintain Requirements"]
    },
    {
      knowledgeArea: "Elicitation and Collaboration",
      severity: "moderate",
      currentScore: 80,
      targetScore: 85,
      recommendedQuestions: 30,
      description: "Strengthen stakeholder engagement techniques and elicitation methods for better collaboration outcomes.",
      specificTopics: ["Stakeholder Engagement", "Elicitation Techniques", "Prepare for Elicitation"]
    }
  ];

  const reviewQuestions = [
    {
      id: 1,
      knowledgeArea: "Requirements Life Cycle Management",
      question: "A business analyst is working on a large-scale enterprise project with multiple stakeholder groups. The requirements have been approved and baselined. However, a key stakeholder requests a significant change that would impact the project timeline and budget. What should the business analyst do first?",
      options: [
        "Immediately update the requirements documentation to reflect the requested change",
        "Assess the impact of the change and follow the established change control process",
        "Reject the change request because requirements have been baselined",
        "Implement the change quickly to maintain stakeholder satisfaction"
      ],
      userAnswer: "Immediately update the requirements documentation to reflect the requested change",
      correctAnswer: "Assess the impact of the change and follow the established change control process",
      isCorrect: false,
      explanation: "When requirements have been baselined and a change is requested, the business analyst must follow the established change control process. This involves assessing the impact of the change on scope, timeline, budget, and other requirements before making any decisions. The change control process ensures that all stakeholders are aware of the implications and that changes are properly evaluated and approved through the appropriate governance structure.",
      babokReference: "BABOK v3, Section 5.4: Maintain Requirements - Change control is a critical aspect of requirements life cycle management that ensures changes are properly evaluated, approved, and implemented."
    },
    {
      id: 2,
      knowledgeArea: "Elicitation and Collaboration",
      question: "During an elicitation session, a business analyst notices that one stakeholder is dominating the conversation while others remain silent. What is the BEST approach to ensure balanced participation?",
      options: [
        "Allow the dominant stakeholder to continue as they seem most knowledgeable",
        "Use facilitation techniques to encourage input from all participants",
        "End the session early and schedule individual meetings with each stakeholder",
        "Ask the dominant stakeholder to leave the session"
      ],
      userAnswer: "Use facilitation techniques to encourage input from all participants",
      correctAnswer: "Use facilitation techniques to encourage input from all participants",
      isCorrect: true,
      explanation: "Effective facilitation is crucial for successful elicitation sessions. When one stakeholder dominates, the business analyst should use facilitation techniques such as round-robin questioning, directed questions to specific individuals, or breaking into smaller groups to ensure all voices are heard. This approach maintains the collaborative nature of the session while ensuring diverse perspectives are captured.",
      babokReference: "BABOK v3, Section 4.2: Conduct Elicitation - Facilitation techniques are essential for managing group dynamics and ensuring productive elicitation sessions."
    },
    {
      id: 3,
      knowledgeArea: "Strategy Analysis",
      question: "An organization is considering implementing a new customer relationship management (CRM) system. Which technique would be MOST appropriate for identifying the current state of customer management processes?",
      options: [
        "Brainstorming sessions with IT department",
        "Process modeling and analysis of existing workflows",
        "Vendor demonstrations of CRM capabilities",
        "Competitive analysis of other companies\' CRM systems"
      ],
      userAnswer: "Process modeling and analysis of existing workflows",
      correctAnswer: "Process modeling and analysis of existing workflows",
      isCorrect: true,
      explanation: "Process modeling and analysis is the most appropriate technique for understanding the current state of customer management processes. This technique allows the business analyst to document existing workflows, identify inefficiencies, and establish a baseline for comparison with the future state. Understanding the current state is essential before evaluating potential solutions.",
      babokReference: "BABOK v3, Section 3.2: Analyze Current State - Process modeling helps identify how work is currently performed and provides a foundation for improvement initiatives."
    }
  ];

  const handleExport = async (option) => {
    console.log(`Exporting ${option?.title} as ${option?.format}`);
    await new Promise(resolve => setTimeout(resolve, 2000));
  };

  const handlePracticeWeakArea = (area) => {
    navigate(`/question-practice?mode=knowledge-area&area=${encodeURIComponent(area?.knowledgeArea)}`);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'knowledge-areas', label: 'Knowledge Areas', icon: 'BookOpen' },
    { id: 'trends', label: 'Performance Trends', icon: 'TrendingUp' },
    { id: 'weak-areas', label: 'Weak Areas', icon: 'Target' },
    { id: 'review', label: 'Question Review', icon: 'FileText' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-[60px]">
        <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
              Results & Analytics
            </h1>
            <p className="text-base md:text-lg text-muted-foreground">
              Comprehensive performance analysis with detailed insights and recommendations
            </p>
          </div>

          <div className="mb-6 md:mb-8">
            <ProgressIndicator
              title="Overall CBAP Preparation Progress"
              current={82}
              total={100}
              showDetails={true}
            />
          </div>

          <div className="mb-6 md:mb-8 overflow-x-auto">
            <div className="flex items-center gap-2 bg-card rounded-lg p-2 shadow-sm border border-border min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-6 md:space-y-8">
            {activeTab === 'overview' && (
              <>
                <ScoreOverview testResult={testResult} />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                  <QuickActionPanel />
                  <ExportActionCenter onExport={handleExport} />
                </div>
              </>
            )}

            {activeTab === 'knowledge-areas' && (
              <KnowledgeAreaBreakdown knowledgeAreas={knowledgeAreas} />
            )}

            {activeTab === 'trends' && (
              <PerformanceTrendChart trendData={trendData} />
            )}

            {activeTab === 'weak-areas' && (
              <WeakAreaIdentification 
                weakAreas={weakAreas}
                onPracticeClick={handlePracticeWeakArea}
              />
            )}

            {activeTab === 'review' && (
              <QuestionReview questions={reviewQuestions} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResultsAnalytics;